import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, OutlinedTextFieldProps } from '@mui/material';
import { IElement } from '@shared/models/project.model';
import {
  useIsSelected,
  useSelectedVersion,
  useUIState,
} from '@client/store/ui/ui.hook';
import { useIsReadonlyElement } from '@client/hooks/use-user.hooks';
import { useToggleElementExpanded } from '@client/hooks/use-expand-elements.hooks';
import { ExpandButton } from '@ui/ElementList/Buttons/ExpandButton';
import { Row, RowCell, useCustomListRowStyles } from '@ui/ElementList';
import {
  ROOT_CELL_WIDTH,
  LIST_SPACING,
  CONTENT_CELL_WIDTH,
  LIST_ITEM_HEIGHT,
  LIST_ITEM_HEIGHT_XL,
} from '@ui/ElementList';
import { useBooleanState } from '@client/hooks/use-utils.hooks';
import {
  useIsMenuOpen,
  useTriggerContextMenu,
} from '@client/hooks/use-menu.hooks';
import { useProjectNavigation } from '@client/hooks/use-router.hooks';
import { useMouseEventCallback } from '@client/hooks/use-events.hooks';
import amplitudeLog from '@client/amplitude';
import { AMP_ELEMENT_SELECT } from '@shared/constants';
import CreateSiblingButton, {
  useShouldShowCreateSiblingButton,
} from '@client/pages/components/CreateSiblingButton';
import { useSortedElementCommments } from '@client/store/comment/comments.hook';

// TODO: get rid of this import from @client
import CommentsIconButton from '@client/pages/components/Comments/CommentsIconButton';

import { useIsDeactivated } from '@client/hooks/use-elements.hooks';
import {
  useResolvedElement,
  useResolvedVersion,
} from '@client/hooks/use-resolved-version.hooks';
import {
  isDeactivatedInElementTree,
  isExpandableElement,
} from '@shared/helpers/element.helpers';
import InlineTextField from '@ui/InlineTextField';
import { useSetNameOfElements } from '@client/hooks/use-element-name.hooks';
import { useApplyProjectChanges } from '@client/store/project';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import { Icon } from '@ui/Icon/Icon';
import { isElement } from '@shared/helpers/recursive-element.helpers';
import {
  getElementCategory,
  isSystemCategory,
  isSystemCategoryElement,
} from '@shared/helpers/element-category.helpers';
import { is } from '@client/helpers/dom.guards';
import { ElementKebabMenu } from '../../menus/ElementKebabMenu';
import ElementBarCharts from '../../charts/ElementBarCharts';
import ElementListItemSBEFLabel from './components/ElementListItemSBEFLabel';
import {
  useElementResults,
  useGetResultsPerGFA,
} from '@features/results/use-results.hooks';
import { useProposalsUsingElement } from '@features/proposals/use-proposals.hooks';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { KebabButtonProps } from '@ui/menus/KebabMenu';
import { CompactValueDisplay } from '../CompactValueDisplay';
import { useFilteredElementChildren } from '@client/hooks/use-filter-elements.hooks';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { useCo2eVisible } from '@features/results/co2e-visible.hook';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { useElementListGroupBy } from '@features/elements/helpers/element-grouping.helpers';
import { getElementIconCategoryName } from '@features/elements/helpers/get-element-category-icon-name.helper';
import { IconButton } from '@ui/buttons';
import { useOpenFocusElement } from '@client/hooks/use-element-focus.hook';
import { ElementQuantityControl } from './components/ElementQuantityControl';
import { useListHover } from '@ui/ElementList';

type ElementListItemInput = {
  element: IElement;
  autoFocus?: boolean;
  indentation?: number;
};

export const ElementListItem: React.FC<ElementListItemInput> = ({
  element,
  autoFocus,
  indentation = 0,
}) => {
  const { setAddedElementId } = useUIState('setAddedElementId');

  const navigateTo = useProjectNavigation();
  const applyChanges = useApplyProjectChanges();
  const setNameOfElements = useSetNameOfElements();
  const toggleElementExpanded = useToggleElementExpanded(element);
  const openFocusElement = useOpenFocusElement();
  const triggerContextMenu = useTriggerContextMenu(element.id);
  const getResultsPerGFA = useGetResultsPerGFA();
  const { classes } = useCustomListRowStyles();

  const selectedVersion = useSelectedVersion();
  const versionForDisplay = useResolvedVersion() ?? selectedVersion;
  const resolvedForList = useResolvedElement(element.id);
  const displayElement = isElement(resolvedForList) ? resolvedForList : element;

  const comments = useSortedElementCommments(element.id);
  const category = getElementCategory(displayElement);
  const proposals = useProposalsUsingElement(element);

  const results = useElementResults(element);
  const resultsPerGFA = getResultsPerGFA(results);
  const co2ePerGFA = getConversionFactorValue(resultsPerGFA, 'co2e');
  const sekPerGFA = getConversionFactorValue(resultsPerGFA, 'sek');

  const readonly = useIsReadonlyElement(element);
  const selected = useIsSelected(element);
  const baselineDeactivated = useIsDeactivated(element);
  const deactivated = versionForDisplay
    ? isDeactivatedInElementTree(versionForDisplay, element.id)
    : baselineDeactivated;
  const showCreateSiblingButton = useShouldShowCreateSiblingButton(element);
  const isKebabMenuOpen = useIsMenuOpen(element.id);
  const displayCurrency = useDisplayCurrency();
  const co2eVisible = useCo2eVisible();

  const isExpandable = isExpandableElement(element);
  const hasVisibleChildren = useFilteredElementChildren(element).length > 0;
  const isExpandAllowed = isExpandable && hasVisibleChildren;
  const [groupById] = useElementListGroupBy();
  const [isEditingName, startEditingName, stopEditingName] =
    useBooleanState(false);
  const { someRowIsHovered, setSomeRowIsHovered } = useListHover();

  const [hover, startHover, endHover] = useBooleanState();
  const [kebabMenuAnchor, setKebabMenuAnchor] = useState<Element>();

  const showMetrics = selected || hover || someRowIsHovered || isKebabMenuOpen;
  const showMetricIcons = selected || hover;
  const showKebabMenu = hover || isKebabMenuOpen || selected;

  const cursiveName = !!category?.cursiveName;

  const nameInputProps: Partial<OutlinedTextFieldProps> = useMemo(
    () => ({
      autoFocus,
      onFocus: (e) => {
        if (autoFocus) {
          e.currentTarget.select();
        }
      },
      onBlur: () => {
        if (autoFocus) {
          setAddedElementId(undefined);
        }
      },
      InputProps: {
        inputProps: {
          sx: {
            padding: '0 4px',
            ' &:not(:focus)::placeholder': {
              opacity: 1, // Show placeholder with no transparency to not confuse with disabled state
            },
          },
        },
      },
    }),
    [autoFocus, setAddedElementId],
  );

  const handleNameInputClick = useMouseEventCallback(
    () => {
      if (selected) startEditingName();
      navigateTo({ elementId: element.id });
    },
    { stopPropagation: false },
  );

  const handleRowClick = useMouseEventCallback(
    () => {
      if (!isKebabMenuOpen || readonly) {
        amplitudeLog(AMP_ELEMENT_SELECT, { ElementId: element.id });
        navigateTo({ elementId: element.id });
      }
    },
    { ignoreInputEvents: true },
  );

  const handleKebabMenuClose = useCallback(() => {
    setKebabMenuAnchor(undefined);
    endHover();
  }, [endHover]);

  const handleSaveName = useCallback(
    async (name: string) => {
      await setNameOfElements(name, element);
      stopEditingName();
    },
    [element, setNameOfElements, stopEditingName],
  );

  const handleMetricsAreaMouseEnter = useCallback(() => {
    setSomeRowIsHovered(true);
  }, [setSomeRowIsHovered]);

  const handleMetricsAreaMouseLeave = useCallback(() => {
    setSomeRowIsHovered(false);
  }, [setSomeRowIsHovered]);

  // Start editing the name if autoFocus is set
  useEffect(() => {
    autoFocus && startEditingName();
  }, [autoFocus, startEditingName]);

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (
        is.Element(e.target) &&
        e.target.closest('input, .MuiInputBase-root') !== null
      ) {
        // abort if user is double-clicking inside an input element
        return;
      }
      if (isSystemCategoryElement(element)) {
        openFocusElement(element.id);
      } else {
        toggleElementExpanded();
      }
    },
    [element, openFocusElement, toggleElementExpanded],
  );

  /**
   * Applies {@link ProjectChangeType.ElementActivate} so the row becomes active again.
   */
  const handleActivateElement = useCallback<React.MouseEventHandler>(() => {
    void applyChanges([
      {
        type: ProjectChangeType.ElementActivate,
        elementId: element.id,
      },
    ]);
  }, [applyChanges, element.id]);

  const leadingIcon = useMemo((): React.ReactNode => {
    if (deactivated) {
      return (
        <IconButton
          disabled={readonly}
          category="mui"
          shape="square"
          size="medium"
          icon="VisibilityOff"
          onClick={handleActivateElement}
        />
      );
    }

    if (!category) return null;
    return (
      <Icon
        category="elementCategory"
        icon={getElementIconCategoryName(category.id)}
        outerSize="large"
        color="text.secondary"
        size={isSystemCategory(category.id) ? 'large' : 'medium'}
      />
    );
  }, [category, deactivated, handleActivateElement, readonly]);

  return (
    <Box
      onMouseEnter={startHover}
      onMouseLeave={endHover}
      onClick={handleRowClick}
      onDoubleClick={handleDoubleClick}
      className={showMetrics ? classes.showMetrics : classes.hideMetrics}
      sx={{
        width: '100%',
      }}
    >
      <Row
        padding
        height={proposals.length < 5 ? LIST_ITEM_HEIGHT : LIST_ITEM_HEIGHT_XL}
        spacing={LIST_SPACING.DEFAULT}
        hover={hover}
        selected={selected}
        deactivated={deactivated}
        onContextMenu={triggerContextMenu}
      >
        <RowCell
          indentation={indentation}
          gap={0}
          width={ROOT_CELL_WIDTH.CONTENT}
        >
          <RowCell gap={0}>
            <ExpandButton
              element={element}
              disabled={!isExpandAllowed}
              hidden={!isExpandAllowed || (!selected && !hover)}
            />
            {leadingIcon}
            {groupById === 'sbef' ? null : (
              <ElementListItemSBEFLabel
                element={element}
                hideIfEmpty={!hover && !selected}
              />
            )}
            <InlineTextField
              autoWidth
              variant="subtitle1"
              value={displayElement.name}
              defaultValue={displayElement.fallbackName}
              strikeThrough={deactivated}
              italic={cursiveName}
              editing={isEditingName}
              readonly={!selected || readonly}
              inactive={deactivated}
              textFieldProps={nameInputProps}
              onClick={handleNameInputClick}
              onSave={handleSaveName}
              onCancel={stopEditingName}
            />

            {comments.length > 0 && <CommentsIconButton comments={comments} />}
          </RowCell>

          <RowCell align="right" width={CONTENT_CELL_WIDTH.EXPRESSION}>
            <ElementQuantityControl
              element={displayElement}
              deactivated={!selected}
            />
          </RowCell>
        </RowCell>
        <RowCell
          responsiveWidth={1}
          onMouseEnter={handleMetricsAreaMouseEnter}
          onMouseLeave={handleMetricsAreaMouseLeave}
        >
          <ElementBarCharts element={element} />

          <RowCell
            id="metrics-cell"
            align="right"
            width={
              CONTENT_CELL_WIDTH.METRICS +
              CONTENT_CELL_WIDTH.ICON_KEBAB +
              // gap between metrics and kebab cells (spacing (2) * mui theme spacing (4) * no. of cells (2))
              LIST_SPACING.DEFAULT * 8
            }
          >
            {co2eVisible.isEnabled && (
              <RowCell align="center" width={CONTENT_CELL_WIDTH.ICON_METRIC}>
                <CompactValueDisplay
                  value={co2ePerGFA}
                  unit="kgCO2e / m²"
                  tooltipTitle="Emissions per GFA"
                  icon={
                    showMetricIcons ? (
                      <Icon category="costEmission" icon="kgCO2e" />
                    ) : undefined
                  }
                />
              </RowCell>
            )}

            {displayCurrency.isEnabled && (
              <RowCell align="center" width={CONTENT_CELL_WIDTH.ICON_METRIC}>
                <CompactValueDisplay
                  value={
                    getDisplayValueData(
                      { value: sekPerGFA, unit: 'sek' },
                      {
                        outputUnit: { unit: displayCurrency.current },
                        notation: 'compact',
                      },
                    ).valueAsNumber
                  }
                  unit={`${displayCurrency.current} / m²`}
                  tooltipTitle="Expenses per GFA"
                  icon={
                    showMetricIcons ? (
                      <Icon
                        category="currency"
                        icon={displayCurrency.current}
                        size={18}
                      />
                    ) : undefined
                  }
                />
              </RowCell>
            )}

            <RowCell
              width={CONTENT_CELL_WIDTH.ICON_KEBAB}
              hideOnPrint
              align="center"
            >
              <ElementKebabMenu
                element={element}
                resolvedElement={displayElement}
                anchor={kebabMenuAnchor}
                buttonProps={kebabButtonProps}
                hidden={!showKebabMenu}
                onOpen={setKebabMenuAnchor}
                onClose={handleKebabMenuClose}
              />
            </RowCell>
          </RowCell>
        </RowCell>
      </Row>

      {showCreateSiblingButton && (
        <CreateSiblingButton element={element} indentation={indentation} />
      )}
    </Box>
  );
};

const kebabButtonProps: Partial<KebabButtonProps> = {
  verticalIcon: true,
  svgProps: { color: 'secondary' },
};

export default memo(ElementListItem);
